import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { QUERY_KEY } from '~common';
import { getRequest } from '~services';
import { inMemoryJWTService } from '~services/jwt.service';
import { CurrentSubscriptionResponse } from '~types/subscription.type';

/**
 * The single source of truth for "what is this user's subscription situation?".
 *
 * Preferred over `/api/subscription/by-phone-number` from the app: both return
 * the same enriched payload, but this one derives the user from the bearer
 * token instead of trusting a phone number in the query string.
 *
 * Pass `imei` so the period is also checked against this station — that way we
 * never offer a swap that eject would refuse. Omitting it skips the station
 * check; `status` and `pass` still come back.
 */
export const useQueryGetCurrentSubscription = (
  reqParams: {
    imei?: string;
  },
  options?: Omit<
    UseQueryOptions<
      CurrentSubscriptionResponse,
      AxiosError<Record<string, string>>,
      CurrentSubscriptionResponse,
      (QUERY_KEY | string)[]
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const getCurrentSubscription = (): Promise<CurrentSubscriptionResponse> =>
    getRequest({
      path: 'api/subscription/current',
      params: reqParams,
    });

  const { enabled = true, ...restOptions } = options ?? {};

  return useQuery(
    [QUERY_KEY.GET_CURRENT_SUBSCRIPTION, reqParams.imei ?? ''],
    () => getCurrentSubscription(),
    {
      // The token is a hard precondition, not a default: a caller passing
      // `enabled` narrows the query, it must never widen it into an
      // unauthenticated call.
      enabled: enabled && !!inMemoryJWTService.getCurrentToken(),
      ...restOptions,
    },
  );
};
